import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiDeleteUser, apiInviteUser, apiListUsers, apiUpdateUser, isApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

type User = {
  id: string;
  email: string;
  name?: string | null;
  role: 'ADMIN' | 'MEMBER';
  isActive: boolean;
  hasPendingInvite?: boolean;
  createdAt?: string;
};

/** Extrait le champ `error` du corps JSON renvoyé par apiFetch, plus robuste que les substrings */
function extractApiError(e: unknown): string {
  if (!(e instanceof Error)) return '';
  const match = e.message.match(/API error \d+: (.+)$/s);
  if (!match) return e.message;
  try {
    return (JSON.parse(match[1]) as { error?: string }).error ?? match[1];
  } catch {
    return match[1];
  }
}

export function SettingsUsers() {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');

  // États pour les actions (modifier rôle / désactiver)
  const [actionUser, setActionUser] = useState<User | null>(null);
  const [actionType, setActionType] = useState<'role' | 'deactivate' | 'delete' | null>(null);
  const [selectedRole, setSelectedRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [actionLoading, setActionLoading] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiListUsers();
      setUsers(res.users || []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '';
      toast.error(msg || 'Impossible de charger les utilisateurs', { duration: Infinity });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiInviteUser({ email: email.trim(), role });
      toast.success(`Invitation envoyée à ${email.trim()}`, { duration: 4000 });
      setEmail('');
      setRole('MEMBER');
      await load();
    } catch (e: unknown) {
      const msg: string = e instanceof Error ? e.message : '';
      if (msg.includes('déjà membre')) {
        toast.error("Cet utilisateur est déjà membre de l'organisation", { duration: Infinity });
      } else if (msg.includes('déjà associé')) {
        toast.error('Cet email est déjà associé à un compte existant', { duration: Infinity });
      } else {
        toast.error(msg || "Erreur lors de l'envoi de l'invitation", { duration: Infinity });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler — modification de rôle (CA-2, CA-5)
  const handleRoleChange = async () => {
    if (!actionUser) return;
    setActionLoading(true);
    try {
      await apiUpdateUser(actionUser.id, { role: selectedRole });
      toast.success('Rôle mis à jour', { duration: 4000 });
      setActionType(null);
      setActionUser(null);
      await load();
    } catch (e: unknown) {
      const msg = extractApiError(e);
      toast.error(
        msg.includes('propre rôle')
          ? 'Vous ne pouvez pas modifier votre propre rôle'
          : msg.includes('administrateur actif')
            ? "L'organisation doit toujours avoir au moins un administrateur actif"
            : 'Erreur lors de la mise à jour du rôle',
        { duration: Infinity }
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handler — désactivation (CA-3, CA-6)
  const handleDeactivate = async () => {
    if (!actionUser) return;
    setActionLoading(true);
    try {
      await apiUpdateUser(actionUser.id, { isActive: false });
      toast.success('Accès désactivé', { duration: 4000 });
      setActionType(null);
      setActionUser(null);
      await load();
    } catch (e: unknown) {
      const msg = extractApiError(e);
      toast.error(
        msg.includes('administrateur actif')
          ? "L'organisation doit toujours avoir au moins un administrateur actif"
          : 'Erreur lors de la désactivation',
        { duration: Infinity }
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handler — réactivation sans dialog (CA-4)
  const handleReactivate = async (u: User) => {
    setReactivatingId(u.id);
    try {
      await apiUpdateUser(u.id, { isActive: true });
      toast.success('Accès réactivé', { duration: 4000 });
      await load();
    } catch {
      toast.error('Erreur lors de la réactivation', { duration: Infinity });
    } finally {
      setReactivatingId(null);
    }
  };

  // Handler — suppression définitive (Story 8.5, droit à l'oubli)
  const handleDelete = async () => {
    if (!actionUser) return;
    const wasPendingInvite = actionUser.hasPendingInvite === true;
    setActionLoading(true);
    try {
      await apiDeleteUser(actionUser.id);
      toast.success(wasPendingInvite ? 'Invitation annulée' : 'Utilisateur supprimé', { duration: 4000 });
      setActionType(null);
      setActionUser(null);
      await load();
    } catch (e: unknown) {
      toast.error(
        isApiError(e) && (e.status === 404 || e.status === 409) ? e.message : "Erreur lors de la suppression de l'utilisateur",
        { duration: Infinity }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (u: User) => {
    setActionUser(u);
    setActionType('delete');
  };

  const openRoleDialog = (u: User) => {
    setActionUser(u);
    setSelectedRole(u.role);
    setActionType('role');
  };

  const openDeactivateDialog = (u: User) => {
    setActionUser(u);
    setActionType('deactivate');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Gestion des utilisateurs</h2>
        <p className="text-sm text-muted-foreground">Les admins peuvent inviter des collaborateurs par email.</p>
      </div>

      <form onSubmit={onInvite} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end bg-white/70 border rounded-lg p-4">
        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="collaborateur@entreprise.com" />
        </div>
        <div>
          <Label>Rôle</Label>
          <select value={role} onChange={(e) => setRole(e.target.value as 'ADMIN' | 'MEMBER')} className="h-9 w-full border rounded px-2 bg-white">
            <option value="MEMBER">Membre</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>
            Envoyer l'invitation
          </Button>
        </div>
      </form>

      <div className="bg-white/70 border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Rôle</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Créé le</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t">
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.name || '-'}</td>
                <td className="p-3">{u.role}</td>
                <td className="p-3">
                  {u.hasPendingInvite ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      En attente
                    </span>
                  ) : u.isActive ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Inactif
                    </span>
                  )}
                </td>
                <td className="p-3">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('fr-FR') : ''}</td>
                <td className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                  {u.hasPendingInvite ? (
                    // Invitation en attente — ni rôle ni désactivation (seul « Supprimer », ci-dessous, l'annule)
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : !u.isActive ? (
                    // Membre désactivé — bouton réactivation
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReactivate(u)}
                      disabled={reactivatingId === u.id}
                    >
                      {reactivatingId === u.id ? 'En cours…' : 'Réactiver'}
                    </Button>
                  ) : u.id === currentUser?.id ? (
                    // C'est l'admin connecté lui-même — "Modifier le rôle" désactivé
                    <Button size="sm" variant="outline" disabled title="Vous ne pouvez pas modifier votre propre rôle">
                      Modifier le rôle
                    </Button>
                  ) : (
                    // Membre actif, pas l'admin connecté
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openRoleDialog(u)}>
                        Modifier le rôle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => openDeactivateDialog(u)}
                      >
                        Désactiver
                      </Button>
                    </div>
                  )}
                  {u.id !== currentUser?.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => openDeleteDialog(u)}
                    >
                      Supprimer
                    </Button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr><td className="p-3 text-muted-foreground" colSpan={6}>Aucun utilisateur.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog — Modifier le rôle */}
      <Dialog open={actionType === 'role'} onOpenChange={(open) => { if (!open) { setActionType(null); setActionUser(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
            <DialogDescription>
              Choisissez le nouveau rôle pour {actionUser?.name || actionUser?.email}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Rôle</Label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'ADMIN' | 'MEMBER')}
              className="mt-1 h-9 w-full border rounded px-2 bg-white"
            >
              <option value="MEMBER">Membre</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionType(null); setActionUser(null); }} disabled={actionLoading}>
              Annuler
            </Button>
            <Button onClick={handleRoleChange} disabled={actionLoading || selectedRole === actionUser?.role}>
              {actionLoading ? 'En cours…' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Désactiver l'accès */}
      <Dialog open={actionType === 'deactivate'} onOpenChange={(open) => { if (!open) { setActionType(null); setActionUser(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver l'accès</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir désactiver l'accès de{' '}
              <strong>{actionUser?.name || actionUser?.email}</strong>{' '}
              ({actionUser?.email}) ? Cette personne ne pourra plus se connecter.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionType(null); setActionUser(null); }} disabled={actionLoading}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeactivate}
              disabled={actionLoading}
            >
              {actionLoading ? 'En cours…' : 'Désactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Supprimer définitivement (Story 8.5) */}
      <Dialog open={actionType === 'delete'} onOpenChange={(open) => { if (!open && !actionLoading) { setActionType(null); setActionUser(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionUser?.hasPendingInvite ? "Annuler l'invitation" : "Supprimer l'utilisateur"}</DialogTitle>
            <DialogDescription>
              {actionUser?.hasPendingInvite ? (
                <>
                  L'invitation envoyée à <strong>{actionUser?.email}</strong> sera annulée : le lien reçu par email ne
                  fonctionnera plus. Vous pourrez inviter cette adresse à nouveau plus tard.
                </>
              ) : (
                <>
                  Supprimer définitivement le compte de{' '}
                  <strong>{actionUser?.name || actionUser?.email}</strong> ({actionUser?.email}) ?{' '}
                  Cette action est immédiate et irréversible : son compte, ses conversations avec l'assistant et ses accès sont supprimés. Les demandes de complément et relances qu'il ou elle a envoyées restent dans l'historique des campagnes, sans son nom.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionType(null); setActionUser(null); }} disabled={actionLoading}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? 'En cours…' : actionUser?.hasPendingInvite ? "Annuler l'invitation" : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
